import {
  Box,
  DialogActions,
  DialogContent,
  Skeleton,
  Stack,
  useMobileBreakpoint,
} from '@customink/pigment-react';

type EditGroupSizesModalSkeletonProps = {
  numberOfItems?: number;
};

export const EditGroupSizesModalSkeleton = ({
  numberOfItems = 3,
}: EditGroupSizesModalSkeletonProps) => {
  const isMobile = useMobileBreakpoint();

  return (
    <>
      <DialogContent
        dividers
        sx={{
          borderTop: 'none',
          mt: 0,
          pt: isMobile ? '0.8125rem' : '1.25rem',
        }}
      >
        <Stack gap="0.5rem">
          <Stack gap="0.5rem">
            {/* Header */}
            <Skeleton height="2rem" variant="text" width="40%" />
            <Skeleton height="1.5rem" variant="text" width="70%" />
          </Stack>

          {/* Design Name */}
          <Skeleton
            height="1.25rem"
            sx={{ mb: 1 }}
            variant="text"
            width="30%"
          />

          {/* Product Items */}
          <Stack gap="1.5rem">
            {[...Array(numberOfItems)].map((_, index) => (
              <Box key={index}>
                <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
                  {/* Product Image */}
                  <Skeleton
                    height="5.625rem"
                    sx={{ borderRadius: '0.25rem' }}
                    variant="rectangular"
                    width="5.625rem"
                  />

                  <Box sx={{ flex: 1 }}>
                    {/* Product Title */}
                    <Skeleton
                      height="1.5rem"
                      sx={{ mb: 0.5 }}
                      variant="text"
                      width="40%"
                    />
                    {/* Color */}
                    <Skeleton
                      height="1.25rem"
                      sx={{ mb: 0.5 }}
                      variant="text"
                      width="15%"
                    />
                    {/* Sizing Tip */}
                    <Skeleton
                      height="1rem"
                      sx={{ opacity: 0.7 }}
                      variant="text"
                      width="25%"
                    />
                  </Box>

                  {/* Remove Link */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Skeleton
                      height="1.25rem"
                      sx={{ opacity: 0.7 }}
                      variant="text"
                      width="3.125rem"
                    />
                  </Box>
                </Stack>

                {/* Size Grid */}
                <Stack spacing={0.5}>
                  {/* Youth/Adult Labels */}
                  <Stack direction="row" spacing={2}>
                    <Skeleton height="1.25rem" variant="text" width="2.5rem" />
                    <Skeleton height="1.25rem" variant="text" width="2.5rem" />
                  </Stack>

                  {/* Size Boxes */}
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {[...Array(12)].map((_, i) => (
                      <Skeleton
                        height="2.25rem"
                        key={i}
                        sx={{ borderRadius: '0.25rem' }}
                        variant="rectangular"
                        width="3.25rem"
                      />
                    ))}
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Stack>
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions
        alignItems="center"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? '1rem' : undefined}
        justifyContent="space-between"
        sx={{
          px: isMobile ? '1rem' : '1.5rem',
        }}
      >
        <Stack direction="row" spacing={1}>
          <Skeleton height="1.25rem" variant="text" width="6.25rem" />
          <Skeleton height="1.25rem" variant="text" width="3.75rem" />
        </Stack>
        <Stack
          alignItems={isMobile ? 'stretch' : 'center'}
          direction={isMobile ? 'column' : 'row'}
          gap={isMobile ? '1rem' : '0.5rem'}
          width={isMobile ? '100%' : 'auto'}
        >
          <Skeleton
            height="2.25rem"
            sx={{ borderRadius: '0.25rem' }}
            variant="rectangular"
            width={isMobile ? '100%' : '5.625rem'}
          />
          <Skeleton
            height="2.25rem"
            sx={{ borderRadius: '0.25rem' }}
            variant="rectangular"
            width={isMobile ? '100%' : '5.625rem'}
          />
        </Stack>
      </DialogActions>
    </>
  );
};
